import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  booleanAttribute,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { HdChartHoverHighlightService } from '../hd-chart-hover-highlight.service';
import { createHdGateHoverHighlight } from '../hd-gate-hover-highlight';
import { HdBodygraphIconKey } from '../hd-bodygraph-icons';
import { HdGateAttributeIconComponent } from '../gate-attributes/hd-gate-attribute-icon.component';
import { HdGateLocksKeysIconComponent } from '../gate-attributes/hd-gate-locks-keys-icon.component';
import { HdMiniCardActivation, resolveHdMiniCardActivation } from './hd-mini-card-activation';

/** Labels die de keynote-regels van het blokje benoemen (zelfde object voor alle blokjes). */
export interface HdPlanetMiniCardKeynoteLabels {
  planet: string;
  gate: string;
  line: string;
  planetTooltip: string;
  gateTooltip: string;
  lineTooltip: string;
}

/** View-model voor één planeet-activatie blokje (gedeeld met de basis-properties parent). */
export interface HdPlanetActivationVm {
  /** Astronomicon-font glyph voor het planeet-icoon. */
  icon: string;
  /** Volledige planeetnaam (vertaald). */
  planet: string;
  /** Thema van de planeet, zoals in de gate-info ("Levensenergie & Expressie"); leeg = geen regel. */
  planetKeynote: string;
  /** Poortnummer (voor de klik → gate-info en de hover-highlight). */
  gate: number;
  /** Poort.lijn, bv. "5.3". */
  gateLine: string;
  /** Eerste keynote van de poort. */
  firstKeynote: string;
  /** Tweede keynote van de poort. */
  secondKeynote: string;
  /** Lijnnummer. */
  lineNumber: number;
  /** Keynote van de specifieke lijn. */
  lineKeynote: string;
  /** Keynote van het hoofdcircuit van deze poort (leeg = geen circuit). */
  circuitKeynote: string;
  /** FontAwesome icon-naam (zonder `fa-` prefix) voor het circuit-thema. */
  circuitIcon: string;
  /** Poort-attribuut-iconen (angst, liefde, geheugen, …) in de volgorde van de gate-info, met tooltip. */
  attributeIcons: readonly { key: HdBodygraphIconKey; label: string }[];
  /** Center van de poort: mini center-icoon in de paletkleur + tooltip met de center-naam. */
  center: { iconHtml: SafeHtml; tooltip: string };
  keynoteLabels: HdPlanetMiniCardKeynoteLabels;
}

/**
 * Planeet-activatie blokje voor het Activaties-paneel.
 * - Links een planeet-icoon (Astronomicon-font) dat over de regels loopt.
 * - Kopregel: planeetnaam + poort.lijn + circuit-icoon; rechts de kenmerken van
 *   de poort zoals in de kop van de gate-info: de poort-attribuut-iconen (angst,
 *   liefde, geheugen, …; klikbaar waar er een paneel bestaat), het lock-/key-
 *   icoon en het mini center-icoon.
 * - Daaronder de keynotes, elk met een label ervoor zodat duidelijk is waar ze
 *   vandaan komen: Planeet (thema van de planeet), Poort (beide poort-keynotes)
 *   en Lijn (lijnnummer + lijn-keynote).
 *
 * Op een smal blokje (telefoon, beide zijden naast elkaar) schuiven de keynotes
 * onder het planeet-icoon door en staan de labels inline voor de tekst.
 *
 * `accentColor` kleurt het icoon en de planeetnaam (rood = design, zwart =
 * personality); de keynotes blijven in een grijstint.
 *
 * Een klik op een icoon in de kopregel opent zijn eigen paneel, niet de
 * gate-info van het blokje. `iconsClickable` uit (gaststand) maakt er gewone
 * iconen van: de klik valt dan door naar het blokje.
 *
 * `interactive` maakt het blokje klikbaar (muis, Enter, spatie → `activated`, de
 * gate-info) met een eenvoudige grijze hover. Met `chartLinked` erbij wordt het een
 * volwaardig grid-element, zoals de blokken van de planeetgrid: hover volgt de
 * gedeelde highlight-regels in beide richtingen (hover hier licht de poort op in
 * chart en grid; hover elders licht dit blokje op of dimt het) en Ctrl/Cmd+klik
 * togglet de freeze. Een informatief blokje (Chiron) doet nooit mee aan de
 * hover-koppeling: Chiron is geen activatie.
 */
@Component({
  selector: 'hd-planet-mini-card',
  templateUrl: './hd-planet-mini-card.component.html',
  styleUrl: './hd-planet-mini-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [HdGateAttributeIconComponent, HdGateLocksKeysIconComponent],
  host: {
    '[class.is-informative]': 'informative()',
    '[class.is-interactive]': 'interactive()',
    '[class.is-chart-linked]': 'hoverLinked()',
    '[class.is-hover-match]': 'isHoverMatch()',
    '[class.is-hover-source]': 'isHoverSource()',
    '[class.is-hover-dimmed]': 'isHoverDimmed()',
    '[attr.role]': 'interactive() ? "button" : null',
    '[attr.tabindex]': 'interactive() ? 0 : null',
    '(click)': 'activate($event)',
    '(keydown.enter)': 'activate($event)',
    '(keydown.space)': 'activate($event)',
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
  },
})
export class HdPlanetMiniCardComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly hoverService = inject(HdChartHoverHighlightService, { optional: true });

  readonly activation = input.required<HdPlanetActivationVm>();
  /** Kleur voor het icoon + de planeetnaam (rood voor design, zwart voor personality). */
  readonly accentColor = input<string>('#000000');
  /** Informatieve positie (Chiron): gedempt en met stippelrand, want géén activatie. */
  readonly informative = input(false, { transform: booleanAttribute });
  /** Klikbaar blokje (rol `button`, focusbaar); activaties komen via `activated`. */
  readonly interactive = input(false, { transform: booleanAttribute });
  /** Gekoppeld aan de chart-hover (highlight in beide richtingen + freeze); enkel samen met `interactive`. */
  readonly chartLinked = input(false, { transform: booleanAttribute });
  /** Iconen in de kopregel openen hun paneel; uit = gewone iconen met tooltip. */
  readonly iconsClickable = input(true, { transform: booleanAttribute });

  readonly activated = output<HdMiniCardActivation>();

  /** Doet mee aan de hover-highlight: een klikbare, gekoppelde, echte activatie (niet Chiron). */
  protected readonly hoverLinked = computed(() => this.interactive() && this.chartLinked() && !this.informative());
  private readonly hostHovered = signal(false);

  /** Gedeelde hover-regels van de grid-kant (match / bron / dim), zie {@link createHdGateHoverHighlight}. */
  private readonly gateHover = createHdGateHoverHighlight(
    this.hoverService,
    () => (this.hoverLinked() ? this.activation().gate : null),
    this.hostHovered,
  );
  protected readonly isHoverMatch = this.gateHover.isMatch;
  protected readonly isHoverSource = this.gateHover.isSource;
  protected readonly isHoverDimmed = this.gateHover.isDimmed;

  /**
   * Hover zet de poort-hover als grid-blok, zoals een blok in de planeetgrid.
   * Bewust zonder keynote-hover: het blokje toont zijn keynotes al zelf, en een
   * keynote-hover met bron grid-blok zou de keynote-kaarten van de grid-blokken
   * met dezelfde poort openklappen.
   */
  protected onMouseEnter(): void {
    if (!this.hoverLinked()) return;
    this.hostHovered.set(true);
    this.hoverService?.setGateHover(this.activation().gate, 'planet-grid-block');
  }

  protected onMouseLeave(): void {
    // Op de eigen hover-staat, niet op `hoverLinked`: wordt de koppeling tijdens een
    // hover uitgezet, dan moet de gezette poort-hover toch nog opgeruimd worden.
    if (!this.hostHovered()) return;
    this.hostHovered.set(false);
    this.hoverService?.setGateHover(null, 'planet-grid-block');
  }

  protected activate(event: Event): void {
    if (!this.interactive()) return;
    // Een icoonknop in het blokje (angst, liefde, lock, …) opent zijn eigen paneel;
    // zijn klik en Enter/spatie bubbelen hierheen en mogen niet ook de gate-info openen.
    const control = event.target instanceof Element ? event.target.closest('button') : null;
    if (control && this.host.nativeElement.contains(control)) return;
    // Spatie zou anders de scrollcontainer van het paneel laten scrollen.
    if (event instanceof KeyboardEvent) event.preventDefault();
    // Ctrl/Cmd+klik = freeze-toggle, zoals op een grid-blok of poort in de chart.
    if (this.hoverLinked() && event instanceof MouseEvent && (event.ctrlKey || event.metaKey)
      && this.hoverService?.enabled()) {
      this.hoverService.toggleFreezeForTarget({ kind: 'gate', gate: this.activation().gate });
      return;
    }
    this.activated.emit(resolveHdMiniCardActivation(event, this.host.nativeElement));
  }
}
